import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth, LoginForm, ForgotPassword, ResetPassword, ChangePasswordModal } from '@/features/auth';
import { RecruitRegistrationForm } from '@/features/registration';
import { OnboardingStep3, OnboardingStep4, OnboardingStep5, OnboardingStep6, OnboardingDocumentsComplete } from '@/features/onboarding';
import { CampaignProvider } from '@/features/campaigns';
import { NotificationProvider, useNotification, setupGlobalNotificationHandler, LoadingSpinner } from '@/shared/components';
import ProtectedRoute from './app/router/components/ProtectedRoute';
import DashboardRouter from './app/router/components/DashboardRouter';


// Component to setup global notification handler
function NotificationSetup() {
  const { showNotification } = useNotification();

  React.useEffect(() => {
    setupGlobalNotificationHandler(showNotification);
  }, [showNotification]);

  return null;
}

// Component to handle global password change modal
function PasswordChangeHandler() {
  const { user, isAuthenticated } = useAuth();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // ✅ Check if password change is required
  useEffect(() => {
    if (isAuthenticated && user?.mustChangePassword) {
      setShowChangePasswordModal(true);
    }
  }, [user, isAuthenticated]);

  // ✅ Global API error handler for password change requirement
  useEffect(() => {
    const handleApiError = (event: any) => {
      if (event.detail?.code === 'PASSWORD_CHANGE_REQUIRED') {
        setShowChangePasswordModal(true);
      }
    };

    window.addEventListener('apiError', handleApiError);
    return () => window.removeEventListener('apiError', handleApiError);
  }, []);

  const handlePasswordChangeSuccess = () => {
    // Update user in localStorage
    const updatedUser = { ...user!, mustChangePassword: false };
    localStorage.setItem('user', JSON.stringify(updatedUser));

    setShowChangePasswordModal(false);
    window.location.reload();
  };

  return (
    <>
      {/* ✅ Global Change Password Modal */}
      {isAuthenticated && (
        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={undefined} // Don't allow closing if forced
          onSuccess={handlePasswordChangeSuccess}
          forceChange={true}
        />
      )}
    </>
  );
}

// Component to handle role-based redirects
function RoleBasedRedirect() {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is a referral recruit who needs to complete onboarding
  // Only applies to users with accountStatus === 'onboarding'
  const isReferralRecruit = user.accountStatus === 'onboarding';
  const onboardingStep = (user as any).onboardingStep;

  if (isReferralRecruit && onboardingStep >= 3 && onboardingStep <= 6) {
    return <Navigate to={`/onboarding/step-${onboardingStep}`} replace />;
  }

  // Redirect based on user role
  switch (user.role) {
    case 'agent':
      return <Navigate to="/agent/dashboard" replace />;
    case 'sales_manager':
      return <Navigate to="/sales/dashboard" replace />;
    case 'hr_manager':
      return <Navigate to="/hr/dashboard" replace />;
    case 'fmo_ceo':
      return <Navigate to="/fmo/dashboard" replace />;
    case 'admin':
      return <Navigate to="/dashboard" replace />;
    case 'user':
      return <Navigate to="/user/dashboard" replace />;
    default:
      return <Navigate to="/dashboard" replace />;
  }
}

function App() {
  return (
    <NotificationProvider>
      <AuthProvider>
        <CampaignProvider>
          <Router>
            <NotificationSetup />
            <PasswordChangeHandler />
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginForm />} />
                <Route path="/register" element={<RecruitRegistrationForm />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* <Route path="/enrollment/:sessionId" element={<PublicEnrollmentForm />} /> */}

                {/* Onboarding Routes - Protected (accessible by all authenticated users) */}
                <Route
                  path="/onboarding/step-3"
                  element={
                    <ProtectedRoute>
                      <OnboardingStep3 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/documents/complete"
                  element={
                    <ProtectedRoute>
                      <OnboardingDocumentsComplete />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/step-4"
                  element={
                    <ProtectedRoute>
                      <OnboardingStep4 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/step-5"
                  element={
                    <ProtectedRoute>
                      <OnboardingStep5 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/step-6"
                  element={
                    <ProtectedRoute>
                      <OnboardingStep6 />
                    </ProtectedRoute>
                  }
                />

                {/* Root route with role-based redirect */}
                <Route path="/" element={<RoleBasedRedirect />} />

                {/* Protected Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardRouter />
                    </ProtectedRoute>
                  }
                />

                {/* Role-specific Protected Routes */}
                <Route
                  path="/agent/*"
                  element={
                    <ProtectedRoute requiredRole="agent">
                      <DashboardRouter />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/sales/*"
                  element={
                    <ProtectedRoute requiredRole="sales_manager">
                      <DashboardRouter />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/hr/*"
                  element={
                    <ProtectedRoute requiredRole="hr_manager">
                      <DashboardRouter />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/fmo/*"
                  element={
                    <ProtectedRoute requiredRole="fmo_ceo">
                      <DashboardRouter />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/user/*"
                  element={
                    <ProtectedRoute requiredRole="user">
                      <DashboardRouter />
                    </ProtectedRoute>
                  }
                />

                {/* Unauthorized Route */}
                <Route
                  path="/unauthorized"
                  element={
                    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
                      <div className="text-center">
                        <h2 className="text-2xl font-bold text-accent-900 mb-2">Access Denied</h2>
                        <p className="text-accent-600 mb-4">You don't have permission to access this page.</p>
                        <button
                          onClick={() => window.history.back()}
                          className="btn-primary"
                        >
                          Go Back
                        </button>
                      </div>
                    </div>
                  }
                />

                {/* Catch-all Route */}
                <Route
                  path="*"
                  element={
                    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
                      <div className="text-center">
                        <h2 className="text-2xl font-bold text-accent-900 mb-2">Page Not Found</h2>
                        <p className="text-accent-600 mb-4">The page you're looking for doesn't exist.</p>
                        <button
                          onClick={() => window.location.href = '/'}
                          className="btn-primary"
                        >
                          Go to Dashboard
                        </button>
                      </div>
                    </div>
                  }
                />
              </Routes>
            </div>
          </Router>
        </CampaignProvider>
      </AuthProvider>
    </NotificationProvider>
  );
}

export default App;
